import { array } from "@msuite/utils/array";
import { guardString } from "@msuite/utils/strings";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { motion } from "motion/react";
import { Logo } from "../branding";
import { type IconType, icon } from "../icons";
import { useTranslation } from "../localization";
import type { ClassNameProps } from "../types";

/** Props Interface */
interface BlankSlateProps extends ClassNameProps {
	/** Draws a dashed outline around the slate. */
	withBorder?: boolean;
	withBackground?: boolean;
	withAccessory?: boolean;
	Icon?: IconType;
	/** The icon's colour, for a slate that names a state rather than an absence. */
	iconColor?: VariantProps<typeof blankSlateIcon>["color"];
	label?: string | null;
	iconRotate?: number;
	onClick?: () => void;
	isLoading?: boolean;
	"data-cy"?: string;
}

export function BlankSlate({
	withBorder = false,
	withBackground = true,
	withAccessory = true,
	className,
	Icon,
	iconColor,
	label: _label,
	iconRotate = 0,
	onClick,
	isLoading = false,
	"data-cy": dataCy,
}: BlankSlateProps) {
	/** Hooks */
	const { t } = useTranslation();

	const BlankSlateIcon = Icon || icon("DiamondMinus");

	const label = guardString(_label, t("general.no_data_available"));

	/** Render */
	return (
		<div
			className={clsx(
				"grow-0 flex-center h-full blank-slate",
				onClick && "cursor-pointer",
				className,
			)}
			onClick={onClick}
			data-cy={dataCy}
		>
			<div
				className={clsx(
					"card",
					"shadow-none bg-transparent",
					"relative flex flex-col flex-center gap-4 w-full h-full",
					onClick && "hover:border-accent-500",
					// One or the other, never both: `border-0` beats `border` whatever their order here.
					withBorder ? "border border-color border-dashed" : "border-0",
					withBackground && "bg-white dark:bg-primaryGray-900",
					"overflow-hidden",
				)}
			>
				<div className="relative flex flex-col justify-center items-center gap-4 pt-4">
					<motion.div
						className="relative"
						animate={{}}
					>
						<motion.div initial={{ rotate: iconRotate }}>
							{!isLoading ? (
								<BlankSlateIcon
									size={30}
									className={blankSlateIcon({ color: iconColor })}
								/>
							) : (
								<Logo
									variant="spinRotation"
									withText={false}
								/>
							)}
						</motion.div>
					</motion.div>
					<div className="relative w-full">
						<p className="text-primaryGray-500 text-center text-sm">{label}</p>
					</div>
					{withAccessory
						? array(5).map((id, index) => (
								<motion.div
									className="absolute border border-color border-dashed rounded-full aspect-square"
									key={id}
									style={{
										inset: `calc(${index + 1} * -7.5rem)`,
										opacity: 1 / (index + 1.5),
									}}
									animate={{
										rotate: [0, index % 2 ? -360 : 360],
									}}
									transition={{
										duration: 45 * (index + 1),
										ease: "linear",
										repeat: Number.POSITIVE_INFINITY,
										repeatType: "loop",
									}}
								/>
							))
						: null}
				</div>
			</div>
		</div>
	);
}

const blankSlateIcon = cva(["stroke-1"], {
	variants: {
		color: {
			accent: ["stroke-accent-500 fill-accent-500/5"],
			emerald: ["stroke-emerald-500 fill-emerald-500/5"],
			red: ["stroke-red-500 fill-red-500/5"],
		},
	},
	defaultVariants: {
		color: "accent",
	},
});
